import type { ArtifactKind, ExecutionMode, Platform, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { submitFormOf, type SubmitForm } from './bindingSubmitModel';

/** Pure model of the build-profile form: defaults, initial state and the small state transitions. No React, no DOM. */

export type ProjectEnvironment = Project['environments'][number];

export interface Binding {
  on: boolean;
  profile: string;
  /** The store app this environment submits to (absent or '' = none); kept as saved when the form does not show it. */
  storeAppId?: string;
  /** How this environment's builds are submitted (P29); absent or null when it does not submit. */
  submit?: SubmitForm | null;
}

const EMPTY_BINDING: Binding = { on: false, profile: '', storeAppId: '', submit: null };
export interface Assertion {
  key: string;
  pattern: string;
}

/** Everything the user can type or pick in the form (UI state such as "advanced is open" lives in the hook). */
export interface ProfileFormValues {
  name: string;
  slug: string;
  platform: Platform;
  providerAccountId: string;
  modes: ExecutionMode[];
  providerProfile: string;
  resourceClass: string;
  artifactKind: ArtifactKind;
  timeout: string;
  bindings: Record<string, Binding>;
  sizeBudgetMb: string;
  bundleId: string;
  versionMonotonic: boolean;
  signingCheck: boolean;
  assertions: Assertion[];
  launchFormJson: string;
}

/** The modes an EAS profile may hold: the CI client (`hangar build`) and Hangar's runner (Run build in the console). */
export const EAS_MODES: ExecutionMode[] = ['ci_client', 'hangar_runner'];
export const BITRISE_MODES: ExecutionMode[] = ['provider_api'];
/** A new EAS profile builds with Hangar's runner, so Run build can start it (the CLI path is docs/mvp-plan.md P17). */
export const DEFAULT_EAS_MODES: ExecutionMode[] = ['hangar_runner'];

export const isBitrise = (kind: string | undefined): boolean => kind === 'bitrise';

/** The execution modes a provider account kind can run (Bitrise: provider API; EAS: CI client and Hangar's runner). */
export const allowedModesFor = (kind: string | undefined): ExecutionMode[] => (isBitrise(kind) ? BITRISE_MODES : EAS_MODES);

/** The modes a profile starts with when its account is picked: EAS -> Hangar's runner, Bitrise -> provider API. */
export const defaultModesFor = (kind: string | undefined): ExecutionMode[] => (isBitrise(kind) ? BITRISE_MODES : DEFAULT_EAS_MODES);

/** The runner is shown but locked while the feature is off (a mode that is already on can still be turned off). */
export const isModeUnavailable = (mode: ExecutionMode, runnerOff: boolean, on: boolean): boolean => mode === 'hangar_runner' && runnerOff && !on;

/**
 * The mode checkboxes the form offers: the CI client is out of the MVP (docs/mvp-plan.md P17 brings it back), so it
 * shows only on a profile that already has it, where it can be turned off.
 */
export const visibleModes = (allowed: ExecutionMode[], current: ExecutionMode[]): ExecutionMode[] => allowed.filter((m) => m !== 'ci_client' || current.includes(m));

/** Adds or removes a mode, keeping the order the user picked them in. */
export const toggleMode = (modes: ExecutionMode[], mode: ExecutionMode, on: boolean): ExecutionMode[] => (on ? [...modes, mode] : modes.filter((m) => m !== mode));

export const artifactKindFor = (platform: Platform): ArtifactKind => (platform === 'ios' ? 'ipa' : 'aab');

/** "store" -> "store-copy" (kept within the 63-character slug limit). */
export const copySlug = (slug: string): string => `${slug.slice(0, 58)}-copy`;

/** The environments a profile can bind to: not archived, in the project's order. */
export function activeEnvironments(environments: ProjectEnvironment[]): ProjectEnvironment[] {
  return [...environments].filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * The provider accounts the select offers. A Bitrise lane leaves the list once the API says Bitrise builds do not run
 * here (P4); the profile's own account stays.
 */
export function selectableAccounts(accounts: ProviderAccount[] | undefined, bitriseFeature: boolean | undefined, sourceAccountId: string | undefined): ProviderAccount[] {
  return (accounts ?? []).filter((a) => a.status !== 'disabled' && (a.kind !== 'bitrise' || bitriseFeature !== false || a.id === sourceAccountId));
}

/** A build service account as a client reads it (SPEC decision 23): the kind in neutral words, numbered when several share it. */
const NEUTRAL_KIND: Record<string, string> = { eas: 'Hangar build service', bitrise: 'Hangar native build service' };

/** The account select's options: the provider account and kind for operators, neutral names for clients. */
export function accountOptions(accounts: Pick<ProviderAccount, 'id' | 'name' | 'kind'>[], seesProviders: boolean): { value: string; label: string }[] {
  if (seesProviders) return accounts.map((a) => ({ value: a.id, label: `${a.name} (${a.kind})` }));
  const seen: Record<string, number> = {};
  const total = (kind: string) => accounts.filter((a) => a.kind === kind).length;
  return accounts.map((a) => {
    seen[a.kind] = (seen[a.kind] ?? 0) + 1;
    const name = NEUTRAL_KIND[a.kind] ?? 'Hangar build service';
    return { value: a.id, label: total(a.kind) > 1 ? `${name} ${seen[a.kind]}` : name };
  });
}

/** The select's placeholder: what to do when there is no account yet (operators connect one; Malible sets it up for clients). */
export const accountPlaceholder = (count: number, seesProviders: boolean): string => {
  if (count > 0) return 'Pick';
  return seesProviders ? 'No Expo account yet: connect one first' : 'No build service yet: your Hangar administrators set it up';
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): string => (typeof v === 'number' ? String(v) : '');
const asRecord = (v: unknown): Record<string, unknown> => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** `providerConfig.workflowByEnvironment` (Bitrise, keyed by environment slug, as in hangar.yml), if any. */
/** The words of the eas.json profile fields (SPEC decision 23): operators keep "eas.json"; clients read "build configuration file". */
export interface BuildConfigCopy {
  label: string;
  hint: string;
  bindingPlaceholder: (envSlug: string) => string;
  bindingLabel: (envName: string) => string;
  emptyBinding: string;
}

export function buildConfigCopy(seesProviders: boolean): BuildConfigCopy {
  if (seesProviders) {
    return {
      label: 'Profile in eas.json', hint: 'The build profile in your build configuration file (eas.json)',
      bindingPlaceholder: (env) => `eas.json profile for ${env}`, bindingLabel: (env) => `Profile in eas.json for ${env}`, emptyBinding: 'An empty eas.json profile uses the one above.',
    };
  }
  return {
    label: 'Profile in the build configuration file (eas.json)', hint: 'The name of a build profile in your build configuration file',
    bindingPlaceholder: (env) => `Profile for ${env}`, bindingLabel: (env) => `Build configuration profile for ${env}`, emptyBinding: 'An empty profile uses the one above.',
  };
}

export const workflowsOf = (config: Record<string, unknown> | undefined): Record<string, unknown> => asRecord(config?.workflowByEnvironment);

/**
 * One binding per environment. EAS: the binding's eas.json profile; Bitrise: the workflow of that environment. A new
 * profile starts bound to every environment except production.
 */
export function initialBindings(environments: ProjectEnvironment[], source: Profile | undefined): Record<string, Binding> {
  return Object.fromEntries(
    environments.map((e) => {
      const bound = source?.environments.find((b) => b.environmentId === e.id);
      const value = str(workflowsOf(source?.providerConfig)[e.slug]) || str(bound?.providerConfig?.profile) || str(bound?.providerConfig?.workflowId);
      return [e.id, { on: source ? Boolean(bound) : e.kind !== 'production', profile: value, storeAppId: bound?.defaultStoreAppId ?? '', submit: submitFormOf(bound?.submit) }];
    }),
  );
}

type VerificationValues = Pick<ProfileFormValues, 'sizeBudgetMb' | 'bundleId' | 'versionMonotonic' | 'signingCheck' | 'assertions'>;

/** The saved `verification` block as form values (version-monotonic is on unless it was switched off). */
export function initialVerification(source: Profile | undefined): VerificationValues {
  const verification = asRecord(source?.verification);
  const raw = Array.isArray(verification.envAssertions) ? (verification.envAssertions as Array<Record<string, unknown>>) : [];
  return {
    sizeBudgetMb: num(verification.sizeBudgetMb),
    bundleId: str(verification.bundleId),
    versionMonotonic: verification.versionMonotonic !== false,
    signingCheck: verification.signing === 'store_app',
    assertions: raw.map((a) => ({ key: str(a.key), pattern: str(a.pattern) })),
  };
}

/**
 * The saved `launchForm` as form values (edited as JSON text). Submit settings live on each environment (the binding); the
 * profile-level `submitDefaults` is left as it is (never sent by the form).
 */
export function initialLaunchForm(source: Profile | undefined): Pick<ProfileFormValues, 'launchFormJson'> {
  const launchForm = source?.launchForm;
  return { launchFormJson: launchForm && Object.keys(launchForm).length > 0 ? JSON.stringify(launchForm, null, 2) : '' };
}

export interface InitialFormInput {
  editing?: Profile;
  duplicateOf?: Profile;
  accounts: ProviderAccount[] | undefined;
  selectable: ProviderAccount[];
  environments: ProjectEnvironment[];
}

function initialIdentity(editing: Profile | undefined, duplicateOf: Profile | undefined): Pick<ProfileFormValues, 'name' | 'slug'> {
  return {
    name: editing?.name ?? (duplicateOf ? `${duplicateOf.name} (copy)` : ''),
    slug: editing?.slug ?? (duplicateOf ? copySlug(duplicateOf.slug) : ''),
  };
}

/** The provider account a new profile starts on: the source's, else the first EAS account, else the first one. */
export const defaultAccountId = (selectable: ProviderAccount[], sourceAccountId: string | undefined): string => sourceAccountId ?? selectable.find((a) => a.kind === 'eas')?.id ?? selectable[0]?.id ?? '';

type ProviderValues = Pick<ProfileFormValues, 'platform' | 'providerAccountId' | 'modes' | 'providerProfile' | 'resourceClass' | 'artifactKind' | 'timeout'>;

function initialProvider(source: Profile | undefined, input: InitialFormInput): ProviderValues {
  const platform: Platform = source?.platform ?? 'android';
  return {
    platform,
    ...initialAccount(source, input),
    artifactKind: source?.artifactKind ?? artifactKindFor(platform),
    timeout: String(source?.timeoutMinutes ?? 120),
    ...initialProviderConfig(source),
  };
}

/** The account and the modes that go with it (the source's own modes, else the account kind's default). */
function initialAccount(source: Profile | undefined, input: InitialFormInput): Pick<ProfileFormValues, 'providerAccountId' | 'modes'> {
  const providerAccountId = defaultAccountId(input.selectable, source?.providerAccountId);
  const kind = input.accounts?.find((a) => a.id === providerAccountId)?.kind;
  return { providerAccountId, modes: source?.executionModes ?? defaultModesFor(kind) };
}

/** The EAS profile and the resource class (EAS) or stack (Bitrise) saved in `providerConfig`. */
function initialProviderConfig(source: Profile | undefined): Pick<ProfileFormValues, 'providerProfile' | 'resourceClass'> {
  const config = source?.providerConfig;
  return { providerProfile: str(config?.profile), resourceClass: str(config?.resourceClass ?? config?.stack) };
}

/** The form's first state: empty for a new profile, prefilled from `editing` or `duplicateOf` (copy slug and name). */
export function initialProfileForm(input: InitialFormInput): ProfileFormValues {
  const source = input.editing ?? input.duplicateOf;
  return {
    ...initialIdentity(input.editing, input.duplicateOf),
    ...initialProvider(source, input),
    bindings: initialBindings(input.environments, source),
    ...initialVerification(source),
    ...initialLaunchForm(source),
  };
}

/** Switching platform also resets the artifact to that platform's default (aab / ipa). */
export const withPlatform = (values: ProfileFormValues, platform: Platform): ProfileFormValues => ({ ...values, platform, artifactKind: artifactKindFor(platform) });

/** Switching account resets the modes to that kind's default. */
export const withAccount = (values: ProfileFormValues, accountId: string, accounts: ProviderAccount[] | undefined): ProfileFormValues => ({
  ...values,
  providerAccountId: accountId,
  modes: defaultModesFor(accounts?.find((a) => a.id === accountId)?.kind),
});

/** Patches one environment's binding (a missing binding starts off and empty). */
export const patchBinding = (bindings: Record<string, Binding>, envId: string, patch: Partial<Binding>): Record<string, Binding> => ({
  ...bindings,
  [envId]: { ...(bindings[envId] ?? EMPTY_BINDING), ...patch },
});

/** Assertion keys are environment-variable names: uppercase letters, digits and underscores. */
export const normalizeAssertionKey = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9_]/g, '_');

export const addAssertion = (assertions: Assertion[]): Assertion[] => [...assertions, { key: '', pattern: '' }];
export const patchAssertion = (assertions: Assertion[], index: number, patch: Partial<Assertion>): Assertion[] => assertions.map((a, j) => (j === index ? { ...a, ...patch } : a));
export const removeAssertion = (assertions: Assertion[], index: number): Assertion[] => assertions.filter((_, j) => j !== index);
